"use client";
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const data = [
  {
    name: "Energy Use /\nDemand",
    currentState: 100,
    withEfficiency: 75,
    currentLabel: "100%",
    efficiencyLabel: "70–80%",
  },
  {
    name: "Energy Cost\n(Bills)",
    currentState: 100,
    withEfficiency: 75,
    currentLabel: "100%",
    efficiencyLabel: "70–80%",
  },
  {
    name: "Reliability\n(Outages)",
    currentState: 60,
    withEfficiency: 90,
    currentLabel: "60%",
    efficiencyLabel: "90%",
  },
  {
    name: "Households Connected\n(by 2030)",
    currentState: 45, // roughly to match "Baseline" height
    withEfficiency: 98, // roughly to match the green bar height
    currentLabel: "Baseline",
    efficiencyLabel: "+5.1\nMillion",
  },
];

const TrendIcon = ({ x, y }: { x: number; y: number }) => (
  <svg
    x={x}
    y={y}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#003350"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
    <polyline points="16 7 22 7 22 13"></polyline>
  </svg>
);

const BillIcon = ({ x, y }: { x: number; y: number }) => (
  <svg
    x={x}
    y={y}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#003350"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const ShieldIcon = ({ x, y }: { x: number; y: number }) => (
  <svg
    x={x}
    y={y}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#003350"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <polyline points="9 12 11 14 15 10"></polyline>
  </svg>
);

const HomeIcon = ({ x, y }: { x: number; y: number }) => (
  <svg
    x={x}
    y={y}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#003350"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const CustomXAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const lines = payload.value.split("\n");

  let Icon: React.FC<{ x: number; y: number }> | null = null;
  if (payload.value.includes("Demand")) Icon = TrendIcon;
  else if (payload.value.includes("Cost")) Icon = BillIcon;
  else if (payload.value.includes("Reliability")) Icon = ShieldIcon;
  else Icon = HomeIcon;

  return (
    <g transform={`translate(${x},${y})`}>
      {Icon && <Icon x={-12} y={5} />}
      <text x={0} y={40} textAnchor="middle" fill="#333" fontSize="13">
        {lines.map((line: string, index: number) => (
          <tspan x={0} dy={index === 0 ? 0 : 16} key={index}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const CustomBarLabel = (props: any) => {
  const { x, y, width, value, index, dataKey } = props;
  const item = data[index];
  const label =
    dataKey === "currentState" ? item.currentLabel : item.efficiencyLabel;
  const lines = label.split("\n");

  return (
    <g transform={`translate(${x + width / 2},${y - 10})`}>
      <text textAnchor="middle" fill="#333" fontSize="11">
        {lines.length === 1 ? (
          <tspan x={0} dy={0}>
            {lines[0]}
          </tspan>
        ) : (
          lines.map((line: string, i: number) => (
            <tspan x={0} dy={i === 0 ? -16 : 16} key={i}>
              {line}
            </tspan>
          ))
        )}
      </text>
    </g>
  );
};

const CustomLegend = (props: any) => {
  return (
    <div className="flex justify-center items-center gap-8 my-2">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-[#003350]"></div>
        <span className="text-[#333] font-medium">Current State</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-[#48DBB2]"></div>
        <span className="text-[#333] font-medium">With Efficiency</span>
      </div>
    </div>
  );
};

export default function GroupedBarChart({ isActive }: { isActive: boolean }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => setShow(true), 200);
    } else {
      setShow(false);
    }
  }, [isActive]);

  return (
    <div className="w-full h-full flex items-center justify-center bg-[#f9f9f9] p-4 font-sans">
      <div className="w-full max-w-[900px] h-[470px] flex flex-col relative">
        <CustomLegend />

        <div className="flex-1 w-full relative">
          {show && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 30, right: 30, left: -20, bottom: 70 }}
                barGap={4}
              >
                <CartesianGrid vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={<CustomXAxisTick />}
                  axisLine={{ stroke: "#9CA3AF" }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  domain={[0, 120]}
                  ticks={[0, 20, 40, 60, 80, 100, 120]}
                  axisLine={{ stroke: "#9CA3AF" }}
                  tickLine={false}
                  tickFormatter={(val) => `${val}%`}
                  tick={{ fill: "#4B5563", fontSize: 13 }}
                />
                <Bar
                  dataKey="currentState"
                  fill="#003350"
                  barSize={35}
                  label={<CustomBarLabel />}
                  isAnimationActive={true}
                />
                <Bar
                  dataKey="withEfficiency"
                  fill="#48DBB2"
                  barSize={35}
                  label={<CustomBarLabel />}
                  isAnimationActive={true}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
